import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { reopenCookieBanner } from "./utils/consentManager";
import axios from "axios";
import {
  LayoutDashboard,
  MessageSquare,
  Mail,
  Phone,
  LogOut,
  Trash2,
  Send,
  RefreshCw,
  ArrowLeft,
  FileText,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITUATION_LABELS = {
  chef_entreprise: "Chef d'entreprise",
  independant: "Indépendant",
  demandeur_emploi: "Demandeur d'emploi",
  public_insertion: "Public en insertion",
  porteur_projet: "Porteur de projet",
  salarie_reconversion: "Salarié en reconversion",
  autre: "Autre",
};
const FINANCEMENT_LABELS = {
  personnel: "Paiement personnel",
  opco: "OPCO",
  cpf: "CPF",
  france_travail: "France Travail",
  ne_sait_pas: "Ne sait pas",
};

// ===============================
// ADMIN LOGIN PAGE
// ===============================
export const AdminLoginPage = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const adminToken = sessionStorage.getItem("adminToken");
    if (adminToken === "admin_authenticated") {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem("adminToken", response.data.token);
        sessionStorage.setItem("adminPassword", password);
        toast.success("Connexion réussie");
        navigate("/admin/dashboard");
      }
    } catch {
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#003087] flex items-center justify-center p-4" data-testid="admin-login">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="font-heading font-extrabold text-2xl text-[#003087]">MGB</span>
            <span className="font-heading font-extrabold text-2xl text-[#F07B1D]">SCHOOL</span>
          </div>
          <CardTitle className="font-heading text-xl text-[#003087]">Espace Administration</CardTitle>
          <CardDescription>Connectez-vous pour accéder au tableau de bord</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-sm font-bold text-[#003087]">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe admin"
                className="mt-1"
                required
                data-testid="admin-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#003087] text-white font-bold hover:bg-[#002266]"
              data-testid="admin-login-btn"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-[#003087]">
              <ArrowLeft className="inline h-4 w-4 mr-1" />
              Retour au site
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ===============================
// ADMIN DASHBOARD PAGE
// ===============================
export const AdminDashboardPage = () => {
  const [stats, setStats] = useState({ contacts_count: 0 });
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const adminToken = sessionStorage.getItem("adminToken");
    if (adminToken !== "admin_authenticated") {
      navigate("/admin");
    }
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/contacts`),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
    } catch {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    sessionStorage.removeItem("adminPassword");
    navigate("/admin");
    toast.success("Déconnexion réussie");
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await axios.delete(`${API}/admin/contact/${contactId}`);
      setContacts(contacts.filter((c) => c.id !== contactId));
      toast.success("Message supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003087] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#003087] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <span className="font-heading font-extrabold text-xl text-white">MGB</span>
              <span className="font-heading font-extrabold text-xl text-[#F07B1D]">SCHOOL</span>
            </div>
            <Badge variant="outline" className="border-white/50 text-white">Administration</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/qualiopi">
              <Button size="sm" className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="admin-qualiopi-btn">
                <FileText className="h-4 w-4 mr-2" />
                Qualiopi & Apprenants
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-white hover:bg-white/10" data-testid="admin-refresh">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#003087] data-[state=active]:text-white">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#003087] data-[state=active]:text-white">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white" data-testid="stat-contacts">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Demandes de contact</p>
                    <p className="font-heading text-4xl font-bold text-[#003087]">{stats.contacts_count}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#003087]/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-[#003087]" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-2">Information</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Les paiements en ligne sont traités par <strong>PayPal</strong> (PayPal et carte bancaire). Chaque paiement crée automatiquement un dossier stagiaire « réglé » avec facture dans l'espace conformité. Consultez <a href="https://www.paypal.com/activities" target="_blank" rel="noopener noreferrer" className="text-[#F07B1D] underline">votre activité PayPal</a> pour les encaissements.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-2">Support</p>
                  <p className="text-sm text-gray-700">
                    Email : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a><br />
                    Tél : 07 58 78 35 74
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent contacts */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-[#003087] flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Dernières demandes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucune demande pour le moment</p>
                ) : (
                  <div className="space-y-3">
                    {contacts.slice(0, 5).map((contact) => (
                      <div key={contact.id} className="p-3 bg-[#F5F5F5] rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-[#003087]">{contact.prenom} {contact.nom}</p>
                          <span className="text-xs text-gray-500">{formatDate(contact.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{contact.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#003087]">Demandes de contact</CardTitle>
                <CardDescription>Toutes les soumissions du formulaire de contact</CardDescription>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune demande pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contacts.map((contact) => (
                      <Card key={contact.id} className="border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h4 className="font-bold text-[#003087]">{contact.prenom} {contact.nom}</h4>
                                <span className="text-xs text-gray-500">{formatDate(contact.created_at)}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-4 w-4" />
                                  <a href={`mailto:${contact.email}`} className="hover:text-[#F07B1D]">{contact.email}</a>
                                </span>
                                {contact.telephone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-4 w-4" />
                                    {contact.telephone}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mb-3">
                                {contact.formation && <Badge variant="outline">Formation : {contact.formation}</Badge>}
                                {contact.situation && <Badge variant="outline" className="border-[#F07B1D]/40 text-[#F07B1D]">{SITUATION_LABELS[contact.situation] || contact.situation}</Badge>}
                                {contact.financement && <Badge variant="outline" className="border-[#2196F3]/40 text-[#2196F3]">{FINANCEMENT_LABELS[contact.financement] || contact.financement}</Badge>}
                              </div>
                              <p className="text-gray-700 bg-[#F5F5F5] p-3 rounded-lg whitespace-pre-wrap">{contact.message}</p>
                              {contact.handicap && (
                                <div className="mt-3 p-3 bg-[#2196F3]/5 border border-[#2196F3]/30 rounded-lg">
                                  <p className="text-xs font-bold text-[#003087] mb-1">Adaptation handicap :</p>
                                  <p className="text-sm text-gray-700">{contact.handicap}</p>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`mailto:${contact.email}`, "_self")}
                                data-testid={`reply-${contact.id}`}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteContact(contact.id)}
                                data-testid={`delete-${contact.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">© 2026 MGB School - Espace Administration</p>
          <span className="flex items-center gap-4">
            <button type="button" onClick={reopenCookieBanner} className="text-sm text-gray-500 hover:underline" data-testid="admin-manage-cookies">Gérer mes cookies</button>
            <Link to="/" className="text-sm text-[#003087] hover:underline">Retour au site public</Link>
          </span>
        </div>
      </footer>
    </div>
  );
};
